import { t } from '@vitesse/base-ui/i18n';

/*
 * How long ago a value last CHANGED, for the grid's freshness cell.
 *
 * Split into a pure half and a translated half. The pure half — the unit ladder
 * and the clock-skew clamp — is where the bugs live and is testable without a DOM
 * or an initialised i18n runtime; `formatAge` is the thin translated wrapper the
 * grid actually calls.
 *
 * COARSE ON PURPOSE. The cell sits beside the value and must never reflow it as
 * the number ticks, so the text has to stay short and fixed-width. It answers
 * "roughly how long", not "precisely when" — the exact instant is in the tooltip,
 * where it costs no layout.
 */

export type AgeUnit = 's' | 'm' | 'h' | 'd';

/** The unit and magnitude for an age in ms. Pure: no i18n, no DOM. */
export function ageParts(ms: number): { n: number; unit: AgeUnit } {
  // Clamp at zero. A producer's clock can sit fractionally ahead of the browser's,
  // and a negative age would render as "-1s" — which reads as a bug rather than as
  // the sub-second skew it actually is.
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return { n: s, unit: 's' };
  const m = Math.floor(s / 60);
  if (m < 60) return { n: m, unit: 'm' };
  const h = Math.floor(m / 60);
  if (h < 24) return { n: h, unit: 'h' };
  return { n: Math.floor(h / 24), unit: 'd' };
}

const AGE_KEYS = {
  s: 'grid.ageSeconds',
  m: 'grid.ageMinutes',
  h: 'grid.ageHours',
  d: 'grid.ageDays',
} as const;

/** Compact relative age, localized. */
export function formatAge(ms: number): string {
  const { n, unit } = ageParts(ms);
  return t(AGE_KEYS[unit], { n });
}
